import PeriodReportAttachmentsEditorWidget from "./PeriodReportAttachmentsEditorWidget.js";
import PeriodReportCommentEditorWidget from "./PeriodReportCommentEditorWidget.js";
import PeriodReportSectionWidgetVisitor from "./PeriodReportSectionWidgetVisitor.js";

/**
 * Visitor that returns an editor renderable for each visited section.
 *
 * All section comments are rendered using an editor.
 *
 * As of today, the attachment section is the only one with a dedicated editor
 * widget.
 *
 * @param {function} spec.onAttachmentUpload - Called with a file as
 *   argument. Should return a promise
 * @param {InputValueActionBuilder} inputValueActionBuilder - Builder for InputValueAction passed
 *   to the WYSIWYG editor
 * @param {function(File):Promise<string>} [onPictureUploadRequest] - Function invoked with
 *   a File to upload to the backend. It resolves to the URL of the uploaded file
 * @param {function(string):Promise<void>} [onSourceDeletionRequest] - Function invoked
 *   with the URL of a file to delete from its storage
 */
export default class PeriodReportSectionEditorVisitor extends PeriodReportSectionWidgetVisitor {
	constructor({
		onAttachmentUpload,
		inputValueActionBuilder,
		onPictureUploadRequest,
		onSourceDeletionRequest,
	}) {
		super(...arguments);

		this._onAttachmentUpload = onAttachmentUpload;

		this._defaultTexts = [];
		this._inputValueActionBuilder = inputValueActionBuilder;

		this._onPictureUploadRequest = onPictureUploadRequest;
		this._onSourceDeletionRequest = onSourceDeletionRequest;
		this._customReports = [];
	}

	setDefaultTexts(defaultTexts) {
		this._defaultTexts = defaultTexts;
	}

	visitAttachmentSection(section) {
		const attachments = new PeriodReportAttachmentsEditorWidget({
			onUpload: this._onAttachmentUpload,
			report: section.getPeriodReport(),
			customReports: this._customReports,
		});

		return this._documentSection({
			section,
			content: attachments,
			dataTestName: "attachments-editor-section",
			dataHelpName: "attachments-editor-section",
		});
	}

	setCustomReports(customReports) {
		this._customReports = customReports;
	}

	_commentRenderer(section) {
		return new PeriodReportCommentEditorWidget({
			defaultTexts: this._defaultTexts,
			accessor: section.getComment.bind(section),
			mutator: section.setComment.bind(section),
			inputValueActionBuilder: this._inputValueActionBuilder,
			onPictureUploadRequest: this._onPictureUploadRequest,
			onSourceDeletionRequest: this._onSourceDeletionRequest,
		});
	}
}
